import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {RESOURCE_BADGE_MAPPING, normalizeResourceBadgeKey} from '../../../constants'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useGlobalBadges} from '../../../contexts/global-elements-context'
import {resolveBadgeCmsStyles} from '../../../utils/global-elements-utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'

const ProductCallout = ({product, styles, intl, props}) => {
    const {app: appConfig} = getConfig()
    const {data: globalBadges} = useGlobalBadges()
    const {site} = useMultiSite()
    const siteId = site?.id
    const badges = product?.representedProduct?.c_textBadges
    const {showPromoSwatches} = props || {}

    const TAGS_COUNT_PRODUCT_TILE = getSiteEnvVarValue(appConfig, 'TAGS_COUNT_PRODUCT_TILE', siteId)
    const maxTags = parseInt(TAGS_COUNT_PRODUCT_TILE, 10) || 1
    const SHOW_FINAL_SALE_TAG_PLP = getSiteEnvVarValue(appConfig, 'SHOW_FINAL_SALE_TAG_PLP', siteId)
    const showFinalSaleOnPLP = SHOW_FINAL_SALE_TAG_PLP === true || SHOW_FINAL_SALE_TAG_PLP === 'true'

    if (!badges?.length) {
        return null
    }

    const validBadges = badges
        .filter((badge) => {
            if (badge === 'finalsale') {
                return showFinalSaleOnPLP
            }
            return Boolean(RESOURCE_BADGE_MAPPING[badge]?.id)
        })
        .sort((a, b) => (a === 'finalsale' ? -1 : b === 'finalsale' ? 1 : 0))
        .slice(0, maxTags)

    if (!validBadges.length) {
        return null
    }

    // On hover, promo swatches replace one row; keep the remaining badge space to prevent layout jumps.
    if (!showPromoSwatches) {
        const preservedRowsCount = Math.max(validBadges.length - 1, 0)

        return Array.from({length: preservedRowsCount}).map((_, index) => (
            <Box
                key={`badge-placeholder-${index}`}
                {...styles.productTileLabelWrapper}
                visibility="hidden"
                aria-hidden="true"
            >
                <Text {...styles.bodyText} {...styles.productTileLabel}>
                    &nbsp;
                </Text>
            </Box>
        ))
    }

    return validBadges.map((badge) => {
        const badgeMessage = RESOURCE_BADGE_MAPPING[normalizeResourceBadgeKey(badge)]

        if (!badgeMessage?.id) {
            return null
        }

        // Look up CMS config for this badge (BadgeId in CMS should match the textBadges value)
        const badgeConfig = globalBadges?.find((b) => b.id === badge)
        const {textStyles = [], borderRadius, opacity, text} = badgeConfig || {}

        const {containerOverrides: cmsWrapperOverrides, badgeOverrides: cmsTextOverrides, hideStyles} =
            resolveBadgeCmsStyles(textStyles, borderRadius, opacity, badgeConfig?.hideBlockOn)

        if (hideStyles.doNotLoadComponent) return null

        const hasCmsOverrides = Object.keys(cmsWrapperOverrides).length > 0
        const hasCmsTextOverrides = Object.keys(cmsTextOverrides).length > 0

        return (
            <Box
                key={badge}
                {...(!hasCmsOverrides && styles.productTileLabelWrapper)}
                {...cmsWrapperOverrides}
                {...(hideStyles.sx && {sx: hideStyles.sx})}
            >
                <Text
                    {...(!hasCmsTextOverrides && styles.bodyText)}
                    {...(!hasCmsTextOverrides && styles.productTileLabel)}
                    className="promo-callout"
                    {...cmsTextOverrides}
                >
                    {/* badgeMessage is a variable, so formatjs can't extract it —
                        ids are registered in ../dynamic-messages.json */}
                    {text || intl.formatMessage(badgeMessage)}
                </Text>
            </Box>
        )
    })
}

ProductCallout.propTypes = {
    product: PropTypes.object,
    styles: PropTypes.object,
    intl: PropTypes.object,
    props: PropTypes.object
}

export default ProductCallout
